import { useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import LeadHeaders from "../components/LeadHeadersDynamic";
import LeadRows from "../components/LeadRowsDynamic";
import ColumnsDropdown from "../components/ColumnsDropdown";
import SortMenu from "../components/SortMenu";
import { setSnackbar } from "../../reducers/snackbarReducer";
import { setLeadColumns } from "../actions/default";
import allColumns from "../columns/columnDefinitions";
import { saveColumnPreferences } from "../api/index";
import { sortBy } from "../../Transactions/utils/helpers";
import AllPeoplePagination from "./AllPeoplePagination";
import { useCurrentUser, useFeatureFlags } from "../../reducers/layoutReducer/selectors";

const prepareOptions = (renderableColumns) =>
  sortBy(
    Object.keys(renderableColumns).map((key) => ({ id: key, ...renderableColumns[key] })),
    "position",
  );

const parseOrderByParam = () => {
  const params = new URLSearchParams(window.location.search);
  const orderBy = params.get("order_by");
  if (!orderBy) return { field: "", direction: "" };
  const parts = orderBy.split(" ");
  const direction = parts.pop();
  const field = parts.join(" ");
  return { field, direction };
};

const LeadIndexDynamic = () => {
  const portalTarget = document.getElementById("lead-columns-dropdown-portal");
  const sortPortalTarget = document.getElementById("lead-sort-dropdown-portal");

  const dispatch = useDispatch();
  const source = useSelector((state) => state.leadIndex.from);
  const autoPlansFeature = useSelector((state) => state.leadIndex.autoPlansFeature);
  const country = useCurrentUser()?.account?.country?.trim() || "";
  // True when its empty/null/undefined or if country does NOT include "ca" (case-insensitive)
  const isUSAddress = !country || !country.toLowerCase().includes("ca");
  const hotLeadsFeature = useSelector((state) => state.leadIndex.hotLeadsFeature);
  const serverRenderableColumns = useSelector((state) => state.leadIndex.renderableColumns);
  const featureFlags = useFeatureFlags();

  // Merge server-saved prefs with defaults, then apply conditional visibility.
  // Filter serverRenderableColumns to known column keys — the backend injects
  // non-column metadata (e.g. view_mode) that would corrupt the column map.
  const renderableColumns = useMemo(() => {
    const columnPrefs = serverRenderableColumns
      ? Object.keys(serverRenderableColumns).reduce((acc, key) => {
          if (allColumns[key]) acc[key] = serverRenderableColumns[key];
          return acc;
        }, {})
      : {};
    const merged = { ...allColumns, ...columnPrefs };

    // Strip any previously-persisted hidden flags so we recompute from scratch,
    // and always re-apply fixed/conditional from allColumns (source of truth).
    const base = Object.keys(merged).reduce((acc, key) => {
      const { hidden, fixed, conditional, ...col } = merged[key];
      acc[key] = {
        ...col,
        // Always use allColumns positions to ensure new columns are ordered correctly
        // even when users have old saved preferences.
        position: col.position ?? allColumns[key]?.position,
        ...(allColumns[key]?.fixed && { fixed: true }),
        ...(allColumns[key]?.conditional && { conditional: allColumns[key].conditional }),
      };
      return acc;
    }, {});

    // Hide conditional columns based on feature flags and scope
    if (!isUSAddress && base.mail) {
      base.mail = { ...base.mail, active: false, hidden: true };
    }
    if (!autoPlansFeature && base.autoPlans) {
      base.autoPlans = { ...base.autoPlans, active: false, hidden: true };
    }
    if (!hotLeadsFeature && base.hotScore) {
      base.hotScore = { ...base.hotScore, active: false, hidden: true };
    }
    if (source !== "collaborators" && source !== "teams") {
      if (base.transactionRole) {
        base.transactionRole = { ...base.transactionRole, active: false, hidden: true };
      }
    }
    if (source !== "teams") {
      if (base.userType) {
        base.userType = { ...base.userType, active: false, hidden: true };
      }
    }

    return base;
  }, [
    serverRenderableColumns,
    isUSAddress,
    featureFlags.sendHandwrittenCard,
    autoPlansFeature,
    hotLeadsFeature,
    source,
  ]);

  // Initialize renderableColumns in Redux if not yet set from server
  useEffect(() => {
    if (!serverRenderableColumns) {
      dispatch(setLeadColumns(allColumns));
    }
  }, []);

  useEffect(() => {
    const text = localStorage.getItem("successMsg");
    if (text)
      dispatch(
        setSnackbar({
          closeOnTimeout: () => {
            dispatch(setSnackbar({ show: false }));
            localStorage.removeItem("successMsg");
          },
          show: true,
          position: "top",
          schema: "executed",
          text,
          timeout: 5000,
        }),
      );
  }, []);

  useEffect(() => {
    const handleReload = () => {
      window.location.reload();
      // TODO: add this back after resolving useMemo issue
      // axios
      //   .get(window.location.href, {
      //     headers: {
      //       Accept: "application/json",
      //       "X-CSRF-Token": window.ReactOnRails?.authenticityToken?.(),
      //     },
      //   })
      //   .then((response) => {
      //     if (response.data?.people) {
      //       dispatch(setLeadPeople(response.data.people));
      //     }
      //   })
      //   .catch(() => {
      //     if (window.Turbolinks) {
      //       window.Turbolinks.cacheCurrentPage();
      //       window.Turbolinks.visit(window.location.href);
      //     }
      //   });
    };

    window.addEventListener("lead-index:reload", handleReload);
    return () => {
      window.removeEventListener("lead-index:reload", handleReload);
    };
  }, [dispatch]);

  const dropdownOptions = useMemo(
    () =>
      prepareOptions(renderableColumns).filter(
        (opt) => !opt.hidden && opt.label && opt.id !== "checkbox" && opt.id !== "showInLeads",
      ),
    [renderableColumns],
  );

  // Build a persistable columns object that preserves server-saved prefs
  // for scope-conditional columns hidden in the current view.
  const buildPersistable = useCallback(
    (columns) =>
      Object.keys(columns).reduce((acc, key) => {
        const { hidden, ...col } = columns[key];
        if (hidden) {
          // Preserve server-saved prefs for columns hidden in this scope;
          // if none exist, omit the key so allColumns defaults apply.
          if (serverRenderableColumns?.[key]) {
            acc[key] = serverRenderableColumns[key];
          }
        } else {
          acc[key] = col;
        }
        return acc;
      }, {}),
    [serverRenderableColumns],
  );

  const toggleColumn = useCallback(
    (columnId) => {
      const updated = {
        ...renderableColumns,
        [columnId]: { ...renderableColumns[columnId], active: !renderableColumns[columnId].active },
      };
      const persistable = buildPersistable(updated);
      dispatch(setLeadColumns(persistable));
      saveColumnPreferences(persistable);
    },
    [renderableColumns, dispatch, buildPersistable],
  );

  const reorderColumn = useCallback(
    (sourceIndex, destinationIndex) => {
      // Perform reorder in the component where we have full column data,
      // then dispatch the result via setLeadColumns (same as toggle).
      const fullSorted = prepareOptions(renderableColumns);
      const srcId = dropdownOptions[sourceIndex]?.id;
      const dstId = dropdownOptions[destinationIndex]?.id;
      const fullSrcIndex = fullSorted.findIndex((c) => c.id === srcId);
      const fullDstIndex = fullSorted.findIndex((c) => c.id === dstId);
      if (fullSrcIndex === -1 || fullDstIndex === -1) return;

      const reordered = [...fullSorted];
      const [removed] = reordered.splice(fullSrcIndex, 1);
      reordered.splice(fullDstIndex, 0, removed);

      // Reassign positions, strip id, and hashify
      const result = reordered.reduce((acc, col, idx) => {
        const { id, ...rest } = col;
        acc[id] = { ...rest, position: idx + 1 };
        return acc;
      }, {});
      const persistable = buildPersistable(result);
      dispatch(setLeadColumns(persistable));
      saveColumnPreferences(persistable);
    },
    [dispatch, renderableColumns, dropdownOptions, buildPersistable],
  );

  const { field: currentSortField, direction: currentSortDirection } = useMemo(parseOrderByParam, []);

  const handleSort = useCallback((field, order) => {
    let url = window.Brivity.addParameter(window.location.href, "order_by", `${field}+${order}`);
    url = window.Brivity.addParameter(url, "order_scope", "");
    window.location.assign(url);
  }, []);

  const columnsDropdown = (
    <ColumnsDropdown options={dropdownOptions} onChange={toggleColumn} onDrag={reorderColumn} />
  );

  const sortMenu = (
    <SortMenu sortField={currentSortField} sortOrder={currentSortDirection} onSave={handleSort} />
  );

  return (
    <div id="content" className="tw-flex tw-flex-col tw-h-full">
      {portalTarget && createPortal(columnsDropdown, portalTarget)}
      {sortPortalTarget && createPortal(sortMenu, sortPortalTarget)}
      <div className="sticky-table tw-flex-1">
        <table className={source === "leads" ? "leads-table" : "all-people-table"}>
          <LeadHeaders renderableColumns={renderableColumns} />
          <LeadRows renderableColumns={renderableColumns} />
        </table>
      </div>
      <AllPeoplePagination />
    </div>
  );
};

export default LeadIndexDynamic;
